import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { money, fileUrl } from "@/lib/api";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, MapPin, KeyRound, User, Loader2, ArrowLeft, ClipboardList, Image as ImageIcon, Receipt, UserPlus, Link2, Trash2, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isViewer } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/properties/${id}`).then(({ data }) => setData(data)).catch(() => setData(false)); }, [id]);

  if (data === null) return <DashboardLayout nav={getNav(user.role)} title="Property"><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div></DashboardLayout>;
  if (!data) return <DashboardLayout nav={getNav(user.role)} title="Property"><p>Not found.</p></DashboardLayout>;

  const { property: p, work_orders, photos, invoices } = data;
  const openWo = work_orders.filter((w) => !["Completed", "Paid", "Closed", "Cancelled"].includes(w.status));
  const closedWo = work_orders.filter((w) => ["Completed", "Paid", "Closed", "Cancelled"].includes(w.status));

  return (
    <DashboardLayout nav={getNav(user.role)} title="Property Details">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 mb-4 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center"><Building2 className="w-6 h-6 text-blue-600" /></div>
              <div>
                <h2 className="font-head text-2xl font-semibold text-slate-900">{p.name}</h2>
                <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.street_address}{p.unit_number ? `, #${p.unit_number}` : ""}, {p.city}, {p.state} {p.zip_code}</p>
              </div>
            </div>
          </div>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${p.occupancy === "Vacant" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{p.occupancy}</span>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div><div className="text-xs uppercase tracking-wide font-bold text-slate-400">Type</div><div className="mt-1 text-slate-800">{p.property_type}</div></div>
          {p.tenant_name && <div><div className="text-xs uppercase tracking-wide font-bold text-slate-400 flex items-center gap-1"><User className="w-3 h-3" /> Tenant</div><div className="mt-1 text-slate-800">{p.tenant_name}<br />{p.tenant_phone}</div></div>}
          {(p.gate_code || p.lockbox_code) && <div><div className="text-xs uppercase tracking-wide font-bold text-slate-400 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Access Codes</div><div className="mt-1 text-slate-800">{p.gate_code && `Gate: ${p.gate_code}`} {p.lockbox_code && `Lockbox: ${p.lockbox_code}`}</div></div>}
          {p.access_instructions && <div className="lg:col-span-2"><div className="text-xs uppercase tracking-wide font-bold text-slate-400">Access</div><div className="mt-1 text-slate-800">{p.access_instructions}</div></div>}
        </div>
        {p.notes && <div className="mt-4 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{p.notes}</div>}
      </div>

      {user.role === "client" && !isViewer && <TenantPortal pid={p.id} propName={p.name} />}

      <Tabs defaultValue="open" className="mt-6">
        <TabsList>
          <TabsTrigger value="open" data-testid="tab-open-wo"><ClipboardList className="w-4 h-4 mr-1" /> Open ({openWo.length})</TabsTrigger>
          <TabsTrigger value="closed">Completed ({closedWo.length})</TabsTrigger>
          <TabsTrigger value="photos"><ImageIcon className="w-4 h-4 mr-1" /> Photos ({photos.length})</TabsTrigger>
          <TabsTrigger value="invoices"><Receipt className="w-4 h-4 mr-1" /> Invoices ({invoices.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="open"><WoList items={openWo} navigate={navigate} /></TabsContent>
        <TabsContent value="closed"><WoList items={closedWo} navigate={navigate} /></TabsContent>
        <TabsContent value="photos">
          {photos.length === 0 ? <Empty text="No photos yet." /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
              {photos.map((ph) => (
                <div key={ph.id} className="rounded-lg overflow-hidden border border-slate-200 bg-white">
                  <img src={fileUrl(ph.storage_path)} alt={ph.phase} className="w-full h-40 object-cover" />
                  <div className="p-2"><span className="text-xs font-semibold capitalize text-slate-600">{ph.phase}</span>{ph.description && <p className="text-xs text-slate-400 truncate">{ph.description}</p>}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="invoices">
          {invoices.length === 0 ? <Empty text="No invoices." /> : (
            <div className="mt-4 space-y-2">
              {invoices.map((iv) => (
                <div key={iv.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-4">
                  <div><div className="font-medium text-slate-900">{iv.invoice_number}</div><div className="text-xs text-slate-500">{iv.description}</div></div>
                  <div className="text-right"><div className="font-head font-semibold">{money(iv.total)}</div><StatusBadge status={iv.status} /></div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function WoList({ items, navigate }) {
  if (items.length === 0) return <Empty text="No work orders." />;
  return (
    <div className="mt-4 space-y-2">
      {items.map((w) => (
        <button key={w.id} data-testid={`wo-row-${w.id}`} onClick={() => navigate(`/app/wo/${w.id}`)} className="w-full text-left flex items-center justify-between bg-white border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
          <div>
            <div className="flex items-center gap-2"><span className="font-medium text-slate-900">{w.wo_number}</span><UrgencyBadge urgency={w.urgency} /></div>
            <div className="text-sm text-slate-500 mt-0.5">{w.service_category} — {w.problem_description}</div>
          </div>
          <StatusBadge status={w.status} />
        </button>
      ))}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-center py-12 text-slate-400 text-sm mt-4 bg-white rounded-xl border border-slate-200">{text}</div>;
}

function TenantPortal({ pid, propName }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [result, setResult] = useState(null);

  const load = () => api.get(`/properties/${pid}/tenant`).then(({ data }) => setState(data)).catch(() => setState({ portal_enabled: false, tenant: null }));
  useEffect(() => { load(); }, [pid]);

  const toggle = async (v) => { try { await api.put(`/properties/${pid}/tenant-portal`, { enabled: v }); load(); toast.success(v ? "Portal enabled" : "Portal disabled"); } catch (e) { toast.error("Failed"); } };
  const invite = async () => {
    if (!f.first_name || !f.email) return toast.error("Name and email required");
    try { const { data } = await api.post(`/properties/${pid}/tenant`, f); setResult(data); setOpen(false); setF({ first_name: "", last_name: "", email: "", phone: "" }); load(); toast.success("Tenant invited"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revoke = async () => { if (!window.confirm("Revoke this tenant's portal access?")) return; try { await api.delete(`/properties/${pid}/tenant`); setResult(null); load(); toast.success("Access revoked"); } catch (e) { toast.error("Failed"); } };
  const regen = async () => { try { const { data } = await api.post(`/properties/${pid}/tenant/magic-link`); setResult({ ...(result || {}), magic_link: data.magic_link }); toast.success("New access link generated"); } catch (e) { toast.error("Failed"); } };
  const copyLink = (link) => { navigator.clipboard?.writeText(`${window.location.origin}${link}`); toast.success("Access link copied"); };

  if (!state) return null;
  const t = state.tenant;

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6" data-testid="tenant-portal-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><User className="w-5 h-5 text-blue-600" /><h3 className="font-head font-semibold text-slate-900">Tenant Portal</h3></div>
        <label className="flex items-center gap-2 text-sm text-slate-500">Enabled <Switch data-testid="tenant-portal-toggle" checked={state.portal_enabled} onCheckedChange={toggle} /></label>
      </div>
      <p className="text-sm text-slate-500 mt-1">Let the tenant of this unit submit and track maintenance requests. They never see owner, financial, or internal data.</p>

      {t ? (
        <div className="mt-4 rounded-lg border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3" data-testid="tenant-portal-active">
          <div>
            <div className="font-medium text-slate-900">{t.first_name} {t.last_name}</div>
            <div className="text-sm text-slate-500">{t.email}{t.phone ? ` · ${t.phone}` : ""}</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={regen} data-testid="tenant-regen-link"><Link2 className="w-3.5 h-3.5 mr-1" /> New Link</Button>
            <Button size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={revoke} data-testid="tenant-revoke-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke</Button>
          </div>
        </div>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid="invite-tenant-btn"><UserPlus className="w-4 h-4 mr-1" /> Invite Tenant</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-head">Invite Tenant — {propName}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First Name</Label><Input data-testid="invite-tenant-first" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Last Name</Label><Input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className="mt-1.5" /></div>
              <div className="col-span-2"><Label>Email</Label><Input data-testid="invite-tenant-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" /></div>
              <div className="col-span-2"><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1.5" /></div>
              <Button data-testid="invite-tenant-save" onClick={invite} className="col-span-2 bg-blue-600 hover:bg-blue-700">Send Invite</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {result?.magic_link && (
        <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm" data-testid="tenant-invite-result">
          <p className="text-slate-700">Share this access link{result.temp_password ? ` (or temp password: ${result.temp_password})` : ""}:</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 truncate bg-white rounded px-2 py-1 text-xs text-slate-600">{window.location.origin}{result.magic_link}</code>
            <Button size="sm" variant="outline" onClick={() => copyLink(result.magic_link)}>Copy</Button>
          </div>
        </div>
      )}
    </div>
  );
}
